'use client'

import Link from 'next/link'
import { HamburgerMenu } from '../hamburger-menu'
import React, { useState, useEffect, useRef } from 'react'
import LoginModal from '@/app/(auth)/_components/modals/login'
import { LikeListModal } from '../modal/LikeList'
import { useLogin } from '@/app/(auth)/_hooks/login/useLogin'
import {
  Listing,
  useCurrentOrderQuery,
  useCurrentUserQuery,
  useListingsLazyQuery,
} from '@/api'
import ShoppingMenu from '@/app/_components/layout/header/shopping-menu'
import { clsx } from 'clsx'
import Image from 'next/image'
import GenerationsLogo from '@/public/events/generations_logo.svg'
import HamburgerIcon from '@/app/_components/icons/HamburgerIcon'
import { usePreOpen } from '@/hooks/usePreOpen'
import { useRouter, useSearchParams } from 'next/navigation'
import CartIcon from '@/app/_components/icons/CartIcon'
import HeartIcon from '@/app/_components/icons/HeartIcon'
import { User, Search } from 'lucide-react'
import { SearchSuggestions } from './SearchSuggestions'
import { useClickOutside } from '@/hooks/useClickOutside'

export function Header() {
  const { isPreOpen } = usePreOpen()
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isSpMenuOpen, setIsSpMenuOpen] = useState(false)
  const [isPcMenuOpen, setIsPcMenuOpen] = useState(false)
  const [isLikeModalOpen, setIsLikeModalOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)

  const [searchListings, { data: searchData }] = useListingsLazyQuery()

  const { data: { currentUser } = {} } = useCurrentUserQuery()
  const { data: { currentOrder } = {} } = useCurrentOrderQuery({
    variables: {
      brandUuid: process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE ?? '',
    },
    skip: !process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE,
    fetchPolicy: 'cache-and-network',
  })

  const {
    open: isLoginOpen,
    setOpen: setLoginOpen,
    loading: isLoginLoading,
    handleLogin,
  } = useLogin()

  const handleOpenLogin = () => {
    setLoginOpen(true)
  }

  const handleClickUserIcon = () => {
    if (currentUser) {
      router.push('/mypage/like')
    } else {
      handleOpenLogin()
    }
  }

  const handleClickHeartIcon = () => {
    if (currentUser) {
      setIsLikeModalOpen(true)
    } else {
      handleOpenLogin()
    }
  }

  const handleClickMypageIcon = () => {
    if (currentUser) {
      setIsSpMenuOpen(false)
      router.push('/mypage/like')
    } else {
      setIsSpMenuOpen(false)
      handleOpenLogin()
    }
  }

  const handleCloseShoppingMenu = () => {
    setIsPcMenuOpen(false)
  }

  const handleCloseHamburgerMenu = () => {
    setIsSpMenuOpen(false)
  }

  const handleCloseSuggestions = () => {
    setShowSuggestions(false)
    setIsSearchOpen(false)
    setSearchKeyword('')
  }

  const buildSearchUrl = (keyword: string) => {
    const params = new URLSearchParams()
    params.set('page', '1')
    params.set('keyword', keyword)

    const filterKeys = ['gender', 'categories', 'conditions', 'sizes', 'sort']
    filterKeys.forEach((key) => {
      const value = searchParams.get(key)
      if (value && (key !== 'sort' || value !== 'recommended')) {
        params.set(key, value)
      }
    })

    return `/listings?${params.toString()}`
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchKeyword.trim()) {
      router.push(buildSearchUrl(searchKeyword))
      handleCloseSuggestions()
    }
  }

  useEffect(() => {
    if (searchKeyword.trim().length >= 1) {
      const timer = setTimeout(() => {
        searchListings({
          variables: {
            brandId: process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE || '',
            page: 1,
            perPage: 4,
            search: {
              keyword: searchKeyword,
            },
          },
        })
        setShowSuggestions(true)
      }, 300)

      return () => clearTimeout(timer)
    } else {
      setShowSuggestions(false)
    }
  }, [searchKeyword, searchListings])

  useClickOutside(
    searchRef,
    handleCloseSuggestions,
    '.search-suggestions-container'
  )

  const likedListings = currentUser?.likedListings || []
  const itemCount = currentOrder?.items?.length || 0
  const suggestions = searchData?.listings?.listings || []

  return (
    <>
      <header
        className={clsx(
          'pl-8 pr-4 lg:px-8 sticky top-0 w-full z-10 transition-colors duration-300 bg-white'
        )}
      >
        <div className="relative">
          <ShoppingMenu
            isOpen={isPcMenuOpen}
            onClose={handleCloseShoppingMenu}
          />
          {isSearchOpen && (
            <div ref={searchRef} className="lg:hidden relative py-2">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  className="w-full px-4 h-8 pr-10 border border-gray-300 rounded-md focus:border-black focus:outline-none text-base"
                  autoFocus
                />
                <button
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  aria-label="検索"
                >
                  <Search className="w-5 h-5" stroke="#000" strokeWidth={1.5} />
                </button>
              </form>
              {showSuggestions && (
                <SearchSuggestions
                  suggestions={suggestions as Listing[]}
                  searchKeyword={searchKeyword}
                  buildSearchUrl={buildSearchUrl}
                  onClose={handleCloseSuggestions}
                  isMobile={true}
                />
              )}
            </div>
          )}
          <div
            className={clsx(
              'relative lg:py-[18px] py-3 flex items-center justify-between',
              isSearchOpen && 'max-lg:hidden'
            )}
          >
            <button
              className="block lg:hidden -ml-4"
              type="button"
              aria-label="Toggle menu"
              onClick={() => setIsSpMenuOpen(!isSpMenuOpen)}
            >
              <HamburgerIcon fill="#000" />
            </button>
            <Link
              href="/"
              className="w-[155px] lg:w-[200px] absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0 lg:ml-2 pr-4 lg:pr-0"
            >
              <h1 className="flex items-center gap-2 text-lg w-auto mx-auto">
                <Image
                  src={GenerationsLogo}
                  alt="CANADA GOOSE GENERATIONS"
                  width={200}
                  height={48}
                  priority
                />
              </h1>
            </Link>
            <nav className="max-lg:hidden flex items-center space-x-8 lg:ml-16">
              {/*　TODO: 本リリース時に isPreOpen 削除　*/}
              {isPreOpen && (
                <>
                  <Link
                    href="/listings?gender=1"
                    className="text-base font-semibold text-black hover:text-black-600"
                  >
                    ウィメンズ
                  </Link>
                  <Link
                    href="/listings?gender=0"
                    className="text-base font-semibold text-black hover:text-black-600"
                  >
                    メンズ
                  </Link>
                  <Link
                    href="/listings?gender=2"
                    className="text-base font-semibold text-black hover:text-black-600"
                  >
                    キッズ
                  </Link>
                </>
              )}
              <Link
                href="/tradein"
                className="text-base font-semibold text-black hover:text-black-600"
              >
                下取り申請
              </Link>

              <Link
                href="/about"
                className="text-base font-semibold text-black hover:text-black-600"
              >
                サービスについて
              </Link>
            </nav>

            <div className="flex items-center space-x-2 lg:space-x-4 ml-auto">
              {/*　TODO: 本リリース時に isPreOpen 削除　*/}
              {isPreOpen && (
                <>
                  {isSearchOpen ? (
                    <div
                      ref={searchRef}
                      className="hidden lg:block relative w-[300px]"
                    >
                      <form
                        onSubmit={handleSearch}
                        className="flex items-center relative"
                      >
                        <input
                          type="text"
                          placeholder="Search"
                          value={searchKeyword}
                          onChange={(e) => setSearchKeyword(e.target.value)}
                          className="w-full px-4 h-8 pr-10 border border-gray-300 rounded-md focus:border-black focus:outline-none text-base"
                          autoFocus
                        />
                        <button
                          type="submit"
                          className="absolute right-3 cursor-pointer"
                          aria-label="検索"
                        >
                          <Search
                            className="w-[18px] h-[18px]"
                            stroke="#000"
                            strokeWidth={1.5}
                          />
                        </button>
                      </form>
                      {showSuggestions && (
                        <SearchSuggestions
                          suggestions={suggestions as Listing[]}
                          searchKeyword={searchKeyword}
                          buildSearchUrl={buildSearchUrl}
                          onClose={handleCloseSuggestions}
                          isMobile={false}
                        />
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      aria-label="検索"
                      onClick={() => setIsSearchOpen(true)}
                      className="cursor-pointer"
                    >
                      <Search
                        className="w-[18px] h-[18px]"
                        stroke="#000"
                        strokeWidth={1.5}
                      />
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="いいね一覧"
                    onClick={handleClickHeartIcon}
                    className="cursor-pointer hidden lg:block"
                  >
                    <HeartIcon
                      className="w-[16px] h-[16px]"
                      fill="#fff"
                      stroke="#000"
                    />
                  </button>
                  <button
                    type="button"
                    aria-label={currentUser ? 'マイページ' : 'ログイン'}
                    onClick={handleClickUserIcon}
                    className="cursor-pointer hidden lg:block"
                  >
                    <User
                      className="w-[18px] h-[18px]"
                      fill="#fff"
                      stroke="#000"
                      strokeWidth={1.5}
                    />
                  </button>
                  <div className="relative">
                    <span
                      className={clsx(
                        'text-black absolute left-1/2 -translate-x-1/2 top-[57%] -translate-y-1/2 text-[10px] -z-10'
                      )}
                    >
                      {itemCount}
                    </span>
                    <Link href="/cart">
                      <CartIcon fill="#000" />
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <HamburgerMenu
          isOpen={isSpMenuOpen}
          onClose={handleCloseHamburgerMenu}
          onMypageClick={handleClickMypageIcon}
        />
      </header>

      <LoginModal
        open={isLoginOpen}
        setOpen={setLoginOpen}
        loading={isLoginLoading}
        onSubmit={handleLogin}
      />

      <LikeListModal
        open={isLikeModalOpen}
        setOpen={setIsLikeModalOpen}
        Listings={likedListings as Listing[]}
      />
    </>
  )
}
